/** UI strings for the F10 models (transitions/Zeeman/spin, shells/X-rays); merged into the photons dictionary. */
export const sv = {
  zmTitle: "Övergångar, Zeemaneffekt och spinnmätning",
  zmNote:
    "Idealt väte med E_n = −13,6 eV/n²: ingen finstruktur, ingen reducerad massa. Zeemanvyn gäller bara banrörelsemängdsmomentet (41.35): spinn, spinn–bankoppling och termen i B² ingår inte.",
  zmViewCascade: "Kaskad",
  zmViewZeeman: "Zeemanuppsplittring",
  zmViewSpin: "Spinnmätning",
  zmUpperN: "Övre tillstånd n",
  zmUpperL: "Bankvanttal l (övre)",
  zmRule: "Regel",
  zmRuleE1: "Dipol (Δl = ±1)",
  zmRuleEnergy: "Bara energi",
  zmCascadeSvg: "Kaskad nedåt i väte",
  zmCascadeDesc:
    "Nivåer ordnade i kolumner efter l (s, p, d, f, g) och i höjd efter n; höjden växer som 1 − 1/n och är inte linjär i energi. Nådda tillstånd är fetare, fångade tillstånd utan tillåten väg nedåt har varningsfärg. Pilar visar varje tillåten kanal nedåt.",
  zmColumn: "l = {l} ({letter})",
  zmStart: "Starttillstånd",
  zmChannels: "Kanaler (pilar)",
  zmLines: "Olika våglängder",
  zmLine: "λ för n = {upper} → {lower}",
  zmTrapped: "Fångade tillstånd utan tillåten väg nedåt",
  zmNone: "inga",
  zmCascadeFormula: String.raw`$hf = E_U - E_L$, $\lambda = hc/(E_U - E_L)$ med $hc = 1239{,}84$ eV nm. Elektrisk dipolövergång kräver $\Delta l = \pm 1$ (§41.4). Samma $(n_U, n_L)$ ger samma våglängd i denna modell oavsett $l$, så kanaler $\neq$ linjer.`,
  zmLowerN: "Undre tillstånd n",
  zmLowerL: "Bankvanttal l (undre)",
  zmField: "Magnetfält B (T)",
  zmZeemanSvg: "Zeemanuppsplittring av övre och undre nivå",
  zmZeemanDesc:
    "Övre nivåns delnivåer överst och undre nivåns nederst, förskjutna med m_l μ_B B (avståndet i bilden växer med B). Pilar visar tillåtna par med Δm_l = −1, 0 eller +1 i tre färger; förbjudna par ritas inte.",
  zmNoAllowed: "Inga tillåtna par: Δl ≠ ±1",
  zmLegend: "Färg: Δm_l = m_U − m_L = −1, 0, +1",
  zmBase: "E₀ utan fält (eV)",
  zmLambda0: "λ₀ utan fält (nm)",
  zmStep: "μ_B B (eV)",
  zmPairs: "Alla par (m_U, m_L)",
  zmAllowed: "Tillåtna par",
  zmForbidden: "Förbjudna par",
  zmDistinct: "Olika fotonenergier",
  zmLineQ: "λ för m_U − m_L = {q} (nm, antal par)",
  zmSpacing: "|Δλ| ≈ λ₀² μ_B B/(hc) (nm)",
  zmRelative: "μ_B B/E₀",
  zmZeemanFormula: String.raw`$U = m_l\mu_B B$ (41.35), $\mu_B = e\hbar/2m = 5{,}788\cdot10^{-5}$ eV/T (41.31). Urvalsregler $\Delta l = \pm1$, $\Delta m_l = 0, \pm1$ (§41.4). Med samma steg $\mu_B B$ i båda nivåerna blir fotonenergin $E_0 + (m_U - m_L)\mu_B B$: tre linjer, oavsett hur många par som är tillåtna.`,
  zmAngle: "Mätaxelns vinkel θ från +z (°)",
  zmCount: "Antal mätningar",
  zmSpinSvg: "Spinn upp längs z mätt längs en lutande axel",
  zmSpinDesc:
    "Vänster: preparerat tillstånd spinn upp längs +z och mätaxeln vid vinkeln θ. Höger: sannolikheterna för spinn upp och spinn ned längs mätaxeln som breda staplar och andelen i de simulerade mätningarna som smala staplar.",
  zmBarPlus: "spinn upp längs axeln",
  zmBarMinus: "spinn ned längs axeln",
  zmBarLegend: "bred: sannolikhet, smal: andel i mätserien",
  zmPlus: "P(spinn upp längs axeln)",
  zmMinus: "P(spinn ned längs axeln)",
  zmMean: "Väntevärde ⟨S_n⟩ (ħ)",
  zmMeanSquare: "⟨S_n²⟩ (ħ²)",
  zmUp: "Utfall spinn upp",
  zmDown: "Utfall spinn ned",
  zmSpinMagnitude: "|S| = √(s(s+1)) (ħ)",
  zmShift: "U för m_s = +½ (spinn upp, μeV)",
  zmSplitting: "Uppsplittring 2U (eV)",
  zmClassical: "Klassisk sfär: v/c vid ekvatorn (r = 1,8 · 10⁻¹⁷ m)",
  zmSpinFormula: String.raw`$S = \sqrt{3/4}\,\hbar$, $S_z = \pm\hbar/2$ (41.36–41.37). $U = \pm(g/2)\mu_B B$ med $g = 2{,}00232$ (41.38, Example 41.6). Mätning längs en axel i vinkeln $\theta$: $P_\pm = \cos^2(\theta/2),\ \sin^2(\theta/2)$ och $\langle S_n\rangle = (\hbar/2)\cos\theta$ (utanför boken). Varje enskild mätning ger $+\hbar/2$ eller $-\hbar/2$.`,
  shTitle: "Pauliprincipen, skärmning och röntgenstrålning",
  shNote:
    "Oberoende elektroner i givna enelektrontillstånd. Pauliprincipen: högst en elektron per tillstånd (n, l, m_l, m_s). Kα och K-tröskeln använder samma enkla skärmningsmodell med Z − 1; modellens K-tröskel ligger över den uppmätta K-kanten (Cu 8,98 keV), som röntgenmodellen använder.",
  shViewAtom: "Elektronkonfiguration",
  shViewOscillator: "Oscillator (Challenge 41.68)",
  shViewKalpha: "Kα och Moseley",
  shViewValence: "Valenselektron och Z_eff",
  shZ: "Atomnummer Z",
  shAtomSvg: "Elektronkonfiguration i grundtillståndet",
  shAtomDesc:
    "En rad per underskal i fyllnadsordning nerifrån (1s till 4p), en ruta per orbital (n, l, m_l). Pilarna visar spinn upp och spinn ned. Till höger antal elektroner per skal mot kapaciteten 2n².",
  shShellRow: "n = {n}: {count} / {capacity}",
  shOrder: "Ordning enligt Table 41.3",
  shElement: "Grundämne",
  shConfiguration: "Konfiguration",
  shStates: "Besatta tillstånd (n, l, m_l, m_s)",
  shPaired: "Orbitaler med två elektroner",
  shValence: "Elektroner i yttersta skalet",
  shException: "Undantag (Cr, Cu)",
  shYes: "ja",
  shNo: "nej",
  shAtomFormula: String.raw`Underskal $(n,l)$ rymmer $2(2l+1)$ elektroner, skal $n$ rymmer $\sum_{l=0}^{n-1}2(2l+1) = 2n^2$ (Problem 41.47). Ordningen 4s före 3d och undantagen Cr ($4s^1 3d^5$) och Cu ($4s^1 3d^{10}$) är tagna ur Table 41.3, inte härledda.`,
  shElectrons: "Antal elektroner",
  shQuantum: "ħω (eV)",
  shPauli: "Pauliprincipen",
  shPauliOn: "Med",
  shPauliOff: "Utan (hypotetiskt)",
  shOscSvg: "Elektroner i en harmonisk oscillator",
  shOscDesc:
    "Parabolisk potentiell energi med nivåerna (v + ½)ħω. Prickarna är elektroner: med Pauliprincipen högst två per nivå (spinn upp och spinn ned), utan den alla i v = 0.",
  shTotalUnits: "E_min (ħω)",
  shTotal: "E_min (eV)",
  shWithout: "Alla i v = 0 (eV)",
  shTop: "Högsta besatta v",
  shAdded: "Nästa elektron kostar (eV)",
  shSquare: "N² för 2N elektroner",
  shOscFormula: String.raw`$E_v = (v+\tfrac12)\hbar\omega$, två spinntillstånd per $v$. För $2N$ elektroner $E_\text{min} = \sum_{v=0}^{N-1} 2(v+\tfrac12)\hbar\omega = N^2\hbar\omega$ (Challenge 41.68). Utan Pauliprincipen vore $E = 2N\cdot\tfrac12\hbar\omega = N\hbar\omega$. Elektronernas växelverkan ingår inte.`,
  shAnodeZ: "Anodens atomnummer Z",
  shVoltage: "Accelerationsspänning U (kV)",
  shKaSvg: "Kα-energi och K-tröskel mot Z",
  shKaDesc:
    "Heldragen kurva: Kα-energin (Z − 1)² · 10,2 eV. Streckad kurva: K-tröskeln (Z − 1)² · 13,6 eV i samma modell. Prickar: uppmätta Kα₁-energier för Fe, Cu och Mo (NIST). Vågrät prickad linje: elektronernas energi eU. Ringen markerar vald anod. Tröskeln är modellens och ligger över den uppmätta K-kanten (Cu 8,98 keV).",
  shKaAxis: "Atomnummer Z (lodrätt: keV)",
  shKaEnergy: "E_Kα i modellen (eV)",
  shKaWavelength: "λ_Kα (nm)",
  shKaFrequency: "f_Kα (Hz)",
  shKaPerZ: "f/(Z − 1)² (Hz)",
  shThreshold: "K-tröskel i modellen (eV)",
  shCutoff: "Kortvågsgräns λ_min = hc/(eU) (nm)",
  shExcites: "eU över K-tröskeln",
  shResidual: "(E_NIST − E_modell)/E_NIST (%)",
  shKaFormula: String.raw`$E_{K\alpha} \approx (Z-1)^2\cdot\tfrac34\cdot 13{,}6$ eV $= (Z-1)^2\cdot 10{,}2$ eV (41.48), $f = (2{,}48\cdot10^{15}\text{ Hz})(Z-1)^2$ (41.47, Moseley). Kortvågsgränsen $\lambda_\text{min} = hc/(eU)$ beror bara på $U$; Kα beror bara på anoden men kräver att $eU$ räcker till att jonisera K-skalet.`,
  shElementPick: "Grundämne",
  shValSvg: "Valensnivåer jämförda med väte",
  shValDesc:
    "Vänster: uppmätta valensnivåer från boken. Höger, streckat: väte med samma n. Energiaxel från 0 nedåt till −6 eV.",
  shValAxis: "Energi (eV)",
  shValLevel: "E({name}) (eV)",
  shValZeff: "Z_eff({name})",
  shValFormula: String.raw`$E_n = -(Z_\text{eff}^2/n^2)(13{,}6\text{ eV})$ (41.45), så $Z_\text{eff} = n\sqrt{|E|/13{,}6\text{ eV}}$. Lägre $l$ ger större sannolikhet nära kärnan och mindre skärmning, alltså större $Z_\text{eff}$ (Examples 41.9–41.10).`,
};

export const en: Record<keyof typeof sv, string> = {
  zmTitle: "Transitions, Zeeman effect and spin measurement",
  zmNote:
    "Ideal hydrogen with E_n = −13.6 eV/n²: no fine structure, no reduced mass. The Zeeman view uses orbital angular momentum only (41.35): spin, spin–orbit coupling and the B² term are left out.",
  zmViewCascade: "Cascade",
  zmViewZeeman: "Zeeman splitting",
  zmViewSpin: "Spin measurement",
  zmUpperN: "Upper state n",
  zmUpperL: "Orbital quantum number l (upper)",
  zmRule: "Rule",
  zmRuleE1: "Dipole (Δl = ±1)",
  zmRuleEnergy: "Energy only",
  zmCascadeSvg: "Downward cascade in hydrogen",
  zmCascadeDesc:
    "Levels arranged in columns by l (s, p, d, f, g) and in height by n; height grows as 1 − 1/n and is not linear in energy. Reached states are bold; trapped states with no allowed way down use the warning colour. Arrows show each allowed downward channel.",
  zmColumn: "l = {l} ({letter})",
  zmStart: "Start state",
  zmChannels: "Channels (arrows)",
  zmLines: "Distinct wavelengths",
  zmLine: "λ for n = {upper} → {lower}",
  zmTrapped: "Trapped states with no allowed way down",
  zmNone: "none",
  zmCascadeFormula: String.raw`$hf = E_U - E_L$, $\lambda = hc/(E_U - E_L)$ with $hc = 1239.84$ eV nm. An electric dipole transition needs $\Delta l = \pm 1$ (§41.4). The same $(n_U, n_L)$ gives the same wavelength in this model whatever $l$, so channels $\neq$ lines.`,
  zmLowerN: "Lower state n",
  zmLowerL: "Orbital quantum number l (lower)",
  zmField: "Magnetic field B (T)",
  zmZeemanSvg: "Zeeman splitting of upper and lower level",
  zmZeemanDesc:
    "Upper-level sublevels at the top and lower-level sublevels at the bottom, shifted by m_l μ_B B (the spacing in the picture grows with B). Arrows show allowed pairs with Δm_l = −1, 0 or +1 in three colours; forbidden pairs are not drawn.",
  zmNoAllowed: "No allowed pairs: Δl ≠ ±1",
  zmLegend: "Colour: Δm_l = m_U − m_L = −1, 0, +1",
  zmBase: "E₀ without field (eV)",
  zmLambda0: "λ₀ without field (nm)",
  zmStep: "μ_B B (eV)",
  zmPairs: "All pairs (m_U, m_L)",
  zmAllowed: "Allowed pairs",
  zmForbidden: "Forbidden pairs",
  zmDistinct: "Distinct photon energies",
  zmLineQ: "λ for m_U − m_L = {q} (nm, number of pairs)",
  zmSpacing: "|Δλ| ≈ λ₀² μ_B B/(hc) (nm)",
  zmRelative: "μ_B B/E₀",
  zmZeemanFormula: String.raw`$U = m_l\mu_B B$ (41.35), $\mu_B = e\hbar/2m = 5.788\cdot10^{-5}$ eV/T (41.31). Selection rules $\Delta l = \pm1$, $\Delta m_l = 0, \pm1$ (§41.4). With the same step $\mu_B B$ in both levels the photon energy is $E_0 + (m_U - m_L)\mu_B B$: three lines, however many pairs are allowed.`,
  zmAngle: "Measurement axis angle θ from +z (°)",
  zmCount: "Number of measurements",
  zmSpinSvg: "Spin up along z measured along a tilted axis",
  zmSpinDesc:
    "Left: the prepared state spin up along +z and the measurement axis at angle θ. Right: the probabilities for spin up and spin down along the axis as wide bars and the fractions in the simulated measurements as narrow bars.",
  zmBarPlus: "spin up along the axis",
  zmBarMinus: "spin down along the axis",
  zmBarLegend: "wide: probability, narrow: fraction in the run",
  zmPlus: "P(spin up along the axis)",
  zmMinus: "P(spin down along the axis)",
  zmMean: "Expectation value ⟨S_n⟩ (ħ)",
  zmMeanSquare: "⟨S_n²⟩ (ħ²)",
  zmUp: "Outcomes spin up",
  zmDown: "Outcomes spin down",
  zmSpinMagnitude: "|S| = √(s(s+1)) (ħ)",
  zmShift: "U for m_s = +½ (spin up, μeV)",
  zmSplitting: "Splitting 2U (eV)",
  zmClassical: "Classical sphere: v/c at the equator (r = 1.8 · 10⁻¹⁷ m)",
  zmSpinFormula: String.raw`$S = \sqrt{3/4}\,\hbar$, $S_z = \pm\hbar/2$ (41.36–41.37). $U = \pm(g/2)\mu_B B$ with $g = 2.00232$ (41.38, Example 41.6). Measurement along an axis at angle $\theta$: $P_\pm = \cos^2(\theta/2),\ \sin^2(\theta/2)$ and $\langle S_n\rangle = (\hbar/2)\cos\theta$ (beyond the book). Each single measurement gives $+\hbar/2$ or $-\hbar/2$.`,
  shTitle: "Exclusion principle, screening and X-rays",
  shNote:
    "Independent electrons in given one-electron states. Exclusion principle: at most one electron per state (n, l, m_l, m_s). Kα and the K threshold use the same simple screening model with Z − 1; the model's K threshold lies above the measured K edge (Cu 8.98 keV), which the X-ray model uses.",
  shViewAtom: "Ground-state configuration",
  shViewOscillator: "Oscillator (Challenge 41.68)",
  shViewKalpha: "Kα and Moseley",
  shViewValence: "Valence electron and Z_eff",
  shZ: "Atomic number Z",
  shAtomSvg: "Ground-state electron configuration",
  shAtomDesc:
    "One row per subshell in filling order from the bottom (1s to 4p), one box per orbital (n, l, m_l). Arrows show spin up and spin down. On the right, electrons per shell against the capacity 2n².",
  shShellRow: "n = {n}: {count} / {capacity}",
  shOrder: "Order from Table 41.3",
  shElement: "Element",
  shConfiguration: "Configuration",
  shStates: "Occupied states (n, l, m_l, m_s)",
  shPaired: "Orbitals with two electrons",
  shValence: "Electrons in the outermost shell",
  shException: "Exception (Cr, Cu)",
  shYes: "yes",
  shNo: "no",
  shAtomFormula: String.raw`Subshell $(n,l)$ holds $2(2l+1)$ electrons, shell $n$ holds $\sum_{l=0}^{n-1}2(2l+1) = 2n^2$ (Problem 41.47). The order 4s before 3d and the exceptions Cr ($4s^1 3d^5$) and Cu ($4s^1 3d^{10}$) are taken from Table 41.3, not derived.`,
  shElectrons: "Number of electrons",
  shQuantum: "ħω (eV)",
  shPauli: "Exclusion principle",
  shPauliOn: "On",
  shPauliOff: "Off (hypothetical)",
  shOscSvg: "Electrons in a harmonic oscillator",
  shOscDesc:
    "Parabolic potential energy with levels (v + ½)ħω. Dots are electrons: with the exclusion principle at most two per level (spin up and spin down), without it all in v = 0.",
  shTotalUnits: "E_min (ħω)",
  shTotal: "E_min (eV)",
  shWithout: "All in v = 0 (eV)",
  shTop: "Highest occupied v",
  shAdded: "Next electron costs (eV)",
  shSquare: "N² for 2N electrons",
  shOscFormula: String.raw`$E_v = (v+\tfrac12)\hbar\omega$, two spin states per $v$. For $2N$ electrons $E_\text{min} = \sum_{v=0}^{N-1} 2(v+\tfrac12)\hbar\omega = N^2\hbar\omega$ (Challenge 41.68). Without the exclusion principle $E = 2N\cdot\tfrac12\hbar\omega = N\hbar\omega$. Electron–electron interaction is left out.`,
  shAnodeZ: "Anode atomic number Z",
  shVoltage: "Accelerating voltage U (kV)",
  shKaSvg: "Kα energy and K threshold against Z",
  shKaDesc:
    "Solid curve: the Kα energy (Z − 1)² · 10.2 eV. Dashed curve: the K threshold (Z − 1)² · 13.6 eV in the same model. Dots: measured Kα₁ energies for Fe, Cu and Mo (NIST). Horizontal dotted line: the electron energy eU. The ring marks the chosen anode. The threshold is the model's and lies above the measured K edge (Cu 8.98 keV).",
  shKaAxis: "Atomic number Z (vertical: keV)",
  shKaEnergy: "E_Kα in the model (eV)",
  shKaWavelength: "λ_Kα (nm)",
  shKaFrequency: "f_Kα (Hz)",
  shKaPerZ: "f/(Z − 1)² (Hz)",
  shThreshold: "K threshold in the model (eV)",
  shCutoff: "Cutoff wavelength λ_min = hc/(eU) (nm)",
  shExcites: "eU above the K threshold",
  shResidual: "(E_NIST − E_model)/E_NIST (%)",
  shKaFormula: String.raw`$E_{K\alpha} \approx (Z-1)^2\cdot\tfrac34\cdot 13.6$ eV $= (Z-1)^2\cdot 10.2$ eV (41.48), $f = (2.48\cdot10^{15}\text{ Hz})(Z-1)^2$ (41.47, Moseley). The cutoff $\lambda_\text{min} = hc/(eU)$ depends only on $U$; Kα depends only on the anode but needs $eU$ large enough to ionize the K shell.`,
  shElementPick: "Element",
  shValSvg: "Valence levels compared with hydrogen",
  shValDesc:
    "Left: measured valence levels from the book. Right, dashed: hydrogen with the same n. Energy axis from 0 down to −6 eV.",
  shValAxis: "Energy (eV)",
  shValLevel: "E({name}) (eV)",
  shValZeff: "Z_eff({name})",
  shValFormula: String.raw`$E_n = -(Z_\text{eff}^2/n^2)(13.6\text{ eV})$ (41.45), so $Z_\text{eff} = n\sqrt{|E|/13.6\text{ eV}}$. Lower $l$ means more probability near the nucleus and less screening, hence larger $Z_\text{eff}$ (Examples 41.9–41.10).`,
};
